import React from 'react';
import styled from 'styled-components';

import { RadiusOption } from 'src/gql/graphql';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import Imgix from 'shared/components/imgix';
import { getCategoryPhoto } from 'shared/helpers/dispensaries';
import { useGetCustomCategoryLink } from 'src/dispensary/hooks/use-get-custom-category-link';
import { ConditionalLink } from 'src/utils/conditional-link';
import { Category } from '../categories-nav.types';
import { categoryCardFocusRing } from './category-card-focus';

const radiusOptions = {
  [RadiusOption.rounded]: '16px',
  [RadiusOption.roundedRectangle]: '12px',
  [RadiusOption.square]: 0,
};

type CompactCardProps = {
  category: Category;
  style: RadiusOption;
};

export const CompactCard = ({ category, style }: CompactCardProps): JSX.Element => {
  const { dispensary } = useDispensary();
  const { key, label, slug, customCategory, cardImage, isOutlet, suppressImage } = category;
  const image = suppressImage ? undefined : cardImage ?? getCategoryPhoto(dispensary, category);
  const imgixParams = {
    width: 224,
  };

  const href = useGetCustomCategoryLink(customCategory, slug, key);

  return (
    <ConditionalLink url={href}>
      <Anchor href={href} $radius={style} data-testid='category-list-link' onClick={category.onClick}>
        <ImageWrapper>
          {image && (
            <Image
              src={image}
              disableSrcSet
              imgixParams={imgixParams}
              htmlAttributes={{ alt: `${label} category image` }}
            />
          )}
        </ImageWrapper>

        <Label $isOutlet={isOutlet}>{label}</Label>
      </Anchor>
    </ConditionalLink>
  );
};

const Anchor = styled.a<{ $radius: RadiusOption }>`
  background: var(--dcm-categories-card-bg);
  border: 1px solid var(--dcm-categories-card-border-color);
  border-radius: ${({ $radius }) => radiusOptions[$radius]};
  color: var(--dcm-categories-card-text-color);
  display: block;
  font-size: 16px;
  font-weight: 600;
  line-height: ${24 / 16};
  padding: 12px;
  text-align: center;
  transition: box-shadow 0.1s;
  white-space: nowrap;
  width: 100%;

  ${categoryCardFocusRing}

  @media screen and (min-width: 600px) {
    font-size: 16px;
    line-height: ${22 / 18};
    padding: 16px;
    width: 156px;
  }

  &:hover {
    box-shadow: var(--dcm-categories-card-hover-shadow);
  }
`;

const ImageWrapper = styled.div`
  align-items: center;
  display: flex;
  height: 51px;
  justify-content: center;
  margin: 0 auto 8px;
  overflow: hidden;
  width: 96px;

  @media screen and (min-width: 600px) {
    height: 60px;
    margin-bottom: 12px;
    width: 112px;
  }
`;

const Image = styled(Imgix)`
  display: block;
  max-height: 100%;
`;

const Label = styled.h3<{ $isOutlet?: boolean }>`
  margin: 0;
  ${({ $isOutlet }) =>
    $isOutlet &&
    `
    overflow: hidden;
    text-overflow: ellipsis;
  `}
`;
